<template>
  <v-container fluid class="pa-0 bg-slate-50 min-h-screen" style="font-family: 'Inter', sans-serif;">
    <div class="p-4 md:p-8">
      <div class="max-w-7xl mx-auto space-y-8">
        
        <!-- HEADER -->
        <div class="mb-8">
          <h1 class="text-2xl font-black text-slate-800 tracking-tight">Gerenciar Notas</h1>
          <p class="text-sm text-slate-500 font-medium">Gestão e controle de notas fiscais de peças e serviços</p>
        </div>

        <!-- FILTROS DE PESQUISA -->
        <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
            <v-icon color="orange" small>mdi-magnify</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
              FILTROS DE PESQUISA
            </h2>
          </div>
          <div class="p-8">
            <v-row>
              <v-col cols="12" sm="12" md="3">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Data Inicial</label>
                <Date
                  @dateValue="[(filter.startDate = $event)]"
                  label="Data Inicial"
                />
              </v-col>
              <v-col cols="12" sm="12" md="3">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Data Final</label>
                <Date @dateValue="[(filter.endDate = $event)]" label="Data Final" />
              </v-col>
              <v-col cols="12" sm="12" md="6">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Prefeitura</label>
                <v-autocomplete
                  :items="prefectures"
                  placeholder="SELECIONE A PREFEITURA..."
                  v-model="filter.prefecture_id"
                  item-value="prefecture_id"
                  item-text="prefecture_social_name"
                  solo flat dense hide-details
                  class="custom-select"
                  height="44"
                  clearable
                  @change="getProviders(filter.prefecture_id)"
                  :disabled="$store.getters.getUserData.table_key == 'prefectures' ? true : false"
                />
              </v-col>
              <v-col cols="12">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Fornecedor</label>
                <v-autocomplete
                  :items="providers"
                  placeholder="SELECIONE O FORNECEDOR..."
                  v-model="filter.provider_id"
                  item-value="provider_id"
                  item-text="provider_name"
                  solo flat dense hide-details
                  class="custom-select"
                  height="44"
                  clearable
                />
              </v-col>
            </v-row>
            <div class="flex justify-end mt-8">
              <button
                @click="sendSearch"
                class="bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow-md shadow-orange-500/10 flex items-center justify-center gap-2 w-fit whitespace-nowrap px-10 h-10 transition-all transform hover:-translate-y-0.5 font-bold text-[11px] uppercase tracking-widest"
              >
                <v-icon color="white" small class="mr-1" style="font-size: 14px;">mdi-filter-outline</v-icon>
                FILTRAR
              </button>
            </div>
          </div>
        </section>

        <!-- LISTAGEM -->
        <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
            <div class="flex items-center gap-3">
              <v-icon color="orange" small>mdi-format-list-bulleted</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                RELAÇÃO DE NOTAS FISCAIS
              </h2>
            </div>
            
            <div class="flex items-center gap-4 flex-1 justify-end">
              <button 
                @click="$router.push('/parts-and-services/nf/create')" 
                class="bg-orange-500 hover:bg-orange-600 text-white shadow-lg flex items-center justify-center gap-2 rounded-lg px-6 py-2 transition-all w-fit max-w-max whitespace-nowrap"
              >
                <v-icon color="white" small>mdi-plus</v-icon>
                <span class="font-bold text-xs uppercase">ADICIONAR</span>
              </button>

              <div class="hidden md:flex relative w-64">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <v-icon color="white" small class="opacity-50">mdi-magnify</v-icon>
                </div>
                <input 
                  v-model="search"
                  placeholder="PESQUISAR..."
                  class="bg-white/10 border border-white/20 rounded-lg py-2 pl-10 pr-4 text-[11px] text-white placeholder:text-white/40 focus:outline-none focus:bg-white/20 transition-all w-full uppercase tracking-widest"
                >
              </div>
            </div>
          </div>

          <div class="p-0">
            <v-data-table
              :headers="headers"
              :items="data"
              :search="search"
              :page.sync="page"
              :items-per-page="itemsPerPage"
              @page-count="pageCount = $event"
              :loading="loader"
              loading-text="CARREGANDO..."
              class="custom-table"
              hide-default-footer
            >
              <template v-slot:[`item.nf_issue_date`]="{ item }">
                <span class="text-xs text-slate-600 font-semibold">{{ convertDate(item.nf_issue_date) }}</span>
              </template>

              <template v-slot:[`item.provider_name`]="{ item }">
                <div class="flex flex-col">
                  <span class="text-sm font-bold text-slate-800 tracking-tight">{{ item.provider_name }}</span>
                  <span class="text-[10px] text-slate-400 font-bold uppercase">{{ item.prefecture_social_name }}</span>
                </div>
              </template>

              <template v-slot:[`item.action`]="{ item }">
                <div class="flex justify-end gap-2">
                  <button 
                    @click="$router.push(`/parts-and-services/nf/edit/${item.nf_id}`)"
                    class="p-2 hover:bg-orange-50 text-slate-400 hover:text-orange-500 rounded-lg transition-all"
                    title="Editar"
                  >
                    <v-icon small color="currentColor">mdi-pencil-outline</v-icon>
                  </button>
                  <button 
                    @click="deleteItem(item.nf_id)"
                    class="p-2 hover:bg-rose-50 text-slate-400 hover:text-rose-500 rounded-lg transition-all"
                    title="Excluir"
                  >
                    <v-icon small color="currentColor">mdi-trash-can-outline</v-icon>
                  </button>
                </div>
              </template>

              <template v-slot:no-data>
                <div class="py-12 flex flex-col items-center justify-center text-slate-400">
                  <v-icon size="48" color="slate-200">mdi-database-off</v-icon>
                  <p class="mt-2 text-sm font-medium">Nenhuma nota fiscal encontrada</p>
                </div>
              </template>
            </v-data-table>

            <!-- Custom Pagination Footer -->
            <div class="bg-slate-50 px-6 py-8 border-t border-slate-200 flex flex-col items-center justify-center gap-6 text-[10px] text-slate-500 font-bold uppercase tracking-widest w-full">
                <span>Exibindo {{ paginationStart }} de {{ data.length }} registros</span>
                
                <div class="flex gap-2">
                    <button 
                      @click="page > 1 ? page-- : null" 
                      :disabled="page === 1"
                      class="px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex items-center gap-1"
                    >
                      <v-icon x-small>mdi-chevron-left</v-icon>
                      ANTERIOR
                    </button>
                    
                    <div class="flex gap-1">
                      <button 
                        v-for="p in visiblePages" 
                        :key="p"
                        @click="page = p"
                        :class="p === page ? 'bg-slate-500 text-white border-slate-500 shadow-md' : 'bg-white border-slate-200 hover:bg-slate-50 text-slate-600'"
                        class="w-10 h-10 border rounded-lg transition-all font-bold flex items-center justify-center"
                      >
                        {{ p }}
                      </button>
                    </div>

                    <button 
                      @click="page < pageCount ? page++ : null" 
                      :disabled="page === pageCount"
                      class="px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex items-center gap-1"
                    >
                      PRÓXIMO
                      <v-icon x-small>mdi-chevron-right</v-icon>
                    </button>
                </div>
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- NOTIFICAÇÕES -->
    <v-snackbar
      v-model="snack.show"
      :color="snack.color"
      timeout="4000"
      top
      elevation="24"
      class="custom-snack"
    >
      <div class="flex items-center gap-3">
        <v-icon color="white" small>{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        <span class="font-bold text-xs uppercase tracking-wider">{{ snack.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false" class="bg-white/10 ml-4 rounded">
          <v-icon size="16">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import Helpers from "@/mixins/helpers.js";
import Date from "@/components/InputDate.vue";
export default {
  mixins: [Helpers],
  components: { Date },
  data: () => ({
    search: "",
    loader: true,
    showSearch: false,
    data: [],
    providers: [],
    prefectures: [],
    filter: {
      endDate: "",
      startDate: "",
      prefecture_id: null,
      provider_id: null,
    },
    snack: {
      show: false,
      message: "",
      color: "",
    },
    headers: [
      { text: "Nota Fiscal", value: "nf_number" },
      { text: "Participante / Prefeitura", value: "provider_name" },
      { text: "Data de Emissão", value: "nf_issue_date" },
      { text: "Ações", align: "right", value: "action", sortable: false },
    ],
    page: 1,
    pageCount: 0,
    itemsPerPage: 8,
  }),
  computed: {
    paginationStart() {
      return (this.page - 1) * this.itemsPerPage + 1;
    },
    visiblePages() {
      const pages = [];
      const totalVisible = 5;
      
      let start = Math.max(1, this.page - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCount, start + totalVisible - 1);

      if (end - start + 1 < totalVisible) {
        start = Math.max(1, end - totalVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  created() {
    if(this.$store.getters.getUserData.table_key == 'prefectures') {
      this.filter.prefecture_id = this.$store.getters.getUserData.table_id;
      this.getProviders(this.filter.prefecture_id);
    }
    this.Helpers;
    this.$http.get("/prefectures").then((r) => (this.prefectures = r.data));
    this.loader = false;
  },
  methods: {
    sendSearch() {
      this.loader = true;
      this.$http
        .post("/parts-and-services/nf/conferences", this.filter)
        .then((r) => {
          this.data = r.data;
          this.loader = false;
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.color = "error";
          this.snack.message = e.message;
          this.loader = false;
        });
    },
    deleteItem(id) {
      let c = confirm("Deseja excluir o registro?");
      if (c) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .delete(`/parts-and-services/nf/${id}`)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.sendSearch(); // Refresh list after delete
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    },
    convertDate(date) {
      if (date) {
        date = date.replace("-", "").replace("-", "");
        const year = date.substring(0, 4);
        const month = date.substring(4, 6);
        const day = date.substring(6, 8);
        return `${day}/${month}/${year}`;
      } else {
        return false;
      }
    },
    getProviders(prefecture_id) {
      this.$http
        .get(`/parts-and-services/providers/prefecture/${prefecture_id}`)
        .then((r) => (this.providers = r.data));
    },
  },
};
</script>

<style scoped>
.custom-table ::v-deep table { 
  background-color: transparent !important; 
}

.custom-table ::v-deep th { 
  color: #64748b !important; 
  font-weight: 900 !important; 
  text-transform: uppercase !important; 
  font-size: 10px !important; 
  letter-spacing: 0.1em !important;
  background-color: #f8fafc !important;
  border-bottom: 2px solid #f1f5f9 !important;
  height: 52px !important;
}

.custom-table ::v-deep td { 
  border-bottom: 1px solid #f8fafc !important;
  color: #475569 !important;
  font-size: 13px !important;
  font-weight: 500 !important;
}

.custom-table ::v-deep tr:hover {
  background-color: #f8fafc !important;
}

.custom-select ::v-deep .v-input__slot {
  background: white !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 8px !important;
  height: 44px !important;
}

.custom-select ::v-deep input {
  font-size: 12px !important;
  font-weight: 600 !important;
  color: #1e293b !important;
  text-transform: uppercase !important;
}
</style>
